import React, { useMemo } from "react";
import { Dialog, Box, TextField, Typography } from "@mui/material";
import { formatFieldLabel } from "@/modules/lead-page/utils";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";
import WithResponsiveRendering from "@/modules/common/components/wrappers/with-responsive-rendering";
import CommonModal from "@/modules/common/components/ui-kit/modals/common-modal";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";

interface ErealtyIframeModalProps {
  open: boolean;
  erealtyFiltersUrl: string;
  handleClose: () => void;
  erealtyIframeOpenCommentModal: boolean;
  setErealtyIframeOpenCommentModal: (value: boolean) => void;
  erealtyIframeComment: string;
  setErealtyIframeComment: (value: string) => void;
  handleErealtyIframeConfirm: (payload?: any) => Promise<void>;
  isLoadingSubmition: boolean;
  erealtyIframePayload?: any;
}

const ErealtyIframeModal: React.FC<ErealtyIframeModalProps> = ({
  open,
  erealtyFiltersUrl,
  handleClose,
  erealtyIframeOpenCommentModal,
  setErealtyIframeOpenCommentModal,
  erealtyIframeComment,
  setErealtyIframeComment,
  handleErealtyIframeConfirm,
  isLoadingSubmition,
  erealtyIframePayload,
}) => {
  const theme = nawyTheme;
  const { isMobile } = useScreenSize();

  const ResponsiveModal = useMemo(
    () =>
      WithResponsiveRendering({
        MobileComponent: CommonModal,
        DesktopComponent: CommonModal,
      }),
    [],
  );

  // Calculate filter count and format display (exclude sale_types)
  const filterCount = useMemo(() => {
    if (!erealtyIframePayload) return 0;

    if (erealtyIframePayload.number_of_compounds !== undefined) {
      return Number(erealtyIframePayload.number_of_compounds);
    }

    let count = 0;
    Object.entries(erealtyIframePayload).forEach(
      ([key, value]: [string, any]) => {
        // Exclude sale_types from count
        if (key.toLowerCase() === "sale_types") return;

        if (Array.isArray(value)) {
          count += value.length;
        } else if (value !== null && value !== undefined) {
          count += 1;
        }
      },
    );
    return count;
  }, [erealtyIframePayload]);

  // Format filter values for display
  const formatFilterValue = (value: any): string => {
    if (Array.isArray(value)) {
      return value
        .map((item) => {
          if (typeof item === "object" && item !== null) {
            return item.name || item.label || item.value || item.id || "";
          }
          return item;
        })
        .filter(Boolean)
        .join(", ");
    } else if (typeof value === "object" && value !== null) {
      return value.name || value.label || value.value || value.id || "";
    }
    return String(value);
  };

  const handleErealtyIframeConfirmAndClose = async () => {
    await handleErealtyIframeConfirm();
    setErealtyIframeOpenCommentModal(false);
    handleClose();
  };

  const handleCancelClick = () => {
    setErealtyIframeOpenCommentModal(false);
    setErealtyIframeComment("");
  };

  const handleModalClose = () => {
    handleCancelClick();
  };

  return (
    <>
      <Dialog
        open={open}
        disableEnforceFocus={erealtyIframeOpenCommentModal}
        onClose={handleClose}
        fullWidth
        maxWidth={false}
        sx={{
          zIndex: 1300,
        }}
        PaperProps={{
          sx: {
            width: "90vw",
            maxWidth: "640px",
            height: "90vh",
            maxHeight: "90vh",
            overflow: "hidden",
            borderRadius: "12px",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            width: "100%",
            padding: isMobile ? 0 : "8px 0 0 8px",
            boxSizing: "border-box",
          }}
        >
          <iframe
            title="E-Realty Filters"
            src={erealtyFiltersUrl}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              borderRadius: "8px",
            }}
          />
        </Box>
      </Dialog>

      {/* Comment Modal with Filter Summary */}
      <ResponsiveModal
        open={erealtyIframeOpenCommentModal}
        onClose={handleModalClose}
        title="Save presentation status"
        cancelButtonText="Cancel"
        confirmButtonText="Save"
        onConfirm={handleErealtyIframeConfirmAndClose}
        isLoading={isLoadingSubmition}
        sx={{
          maxWidth: "600px",
          width: "100%",
          zIndex: 2000,
        }}
      >
        <Box
          sx={{
            px: theme.nawy.spacing.md,
            pb: 0,
            width: "100%",
          }}
        >
          {/* Filter Summary */}
          {erealtyIframePayload && (
            <Box sx={{ mb: theme.nawy.spacing.lg, mt: theme.nawy.spacing.md }}>
              <Typography
                sx={{
                  color: theme.nawy.colors.semantic.text.primary,
                  fontSize:
                    theme.nawy.typography.english.variants.body.sm.regular
                      .fontSize,
                  fontWeight:
                    theme.nawy.typography.english.variants.body.sm.regular
                      .fontWeight,
                  mb: theme.nawy.spacing.sm,
                }}
              >
                You filtered:{" "}
                <Box
                  component="span"
                  sx={{
                    fontWeight:
                      theme.nawy.typography.english.variants.body.sm.semibold
                        .fontWeight,
                  }}
                >
                  {filterCount} results
                </Box>{" "}
              </Typography>

              {/* Display Filters (exclude sale_types) */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: theme.nawy.spacing.xs,
                }}
              >
                {Object.entries(erealtyIframePayload).map(
                  ([key, value]: [string, any]) => {
                    // Exclude sale_types from display
                    if (
                      key.toLowerCase() === "sale_types" ||
                      key === "number_of_compounds"
                    )
                      return null;

                    const displayValue = formatFilterValue(value);
                    if (!displayValue) return null;

                    return (
                      <Typography
                        key={key}
                        sx={{
                          fontSize:
                            theme.nawy.typography.english.variants.body.sm
                              .regular.fontSize,
                          color: theme.nawy.colors.semantic.text.primary,
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            fontWeight:
                              theme.nawy.typography.english.variants.body.sm
                                .semibold.fontWeight,
                          }}
                        >
                          {formatFieldLabel(key)}:
                        </Box>{" "}
                        <Box
                          component="span"
                          sx={{
                            fontWeight:
                              theme.nawy.typography.english.variants.body.sm
                                .regular.fontWeight,
                          }}
                        >
                          {displayValue}
                        </Box>
                      </Typography>
                    );
                  },
                )}
              </Box>
            </Box>
          )}

          {/* Comment Field */}
          <Box sx={{ width: "100%" }}>
            <Typography
              sx={{
                fontSize:
                  theme.nawy.typography.english.variants.body.sm.regular
                    .fontSize,
                fontWeight:
                  theme.nawy.typography.english.variants.body.sm.regular
                    .fontWeight,
                color: theme.nawy.colors.semantic.text.primary,
                mb: theme.nawy.spacing.xs,
              }}
            >
              Comment (optional)
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Add comment"
              value={erealtyIframeComment}
              onChange={(e) => setErealtyIframeComment(e.target.value)}
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#FFFFFF",
                },
              }}
            />
          </Box>
        </Box>
      </ResponsiveModal>
    </>
  );
};

export default ErealtyIframeModal;
